import { king, SUITS } from "@app/rules";
import { useState } from "react";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";
import { SUIT_GLYPH } from "./Card";
import { Key, OptionTile, Sheet } from "./Sheet";

type Game = king.Penalty | "trump";

const GAMES: readonly Game[] = [...king.PENALTIES, "trump"];

/** Why a game cannot be named now: the table has played it twice, or your own rights are spent. */
function closedReason(view: king.KingView, game: Game): "gone" | "noRight" | null {
  const choices = view.choices;
  if (choices === null) return "noRight";
  if (game === "trump") return choices.trump ? null : "noRight";
  if (choices.penalties.includes(game)) return null;
  return view.penaltiesLeft[game] <= 0 ? "gone" : "noRight";
}

/** One game on offer: its name, what it costs or pays, and how often the table may still play it. */
function GameKey({
  view,
  game,
  dense,
  onPick,
}: {
  view: king.KingView;
  game: Game;
  dense: boolean;
  onPick: (game: Game) => void;
}) {
  const { t } = useT();
  const name = t(`king.game.${game}`);
  const reason = closedReason(view, game);
  const left = game === "trump" ? null : t("king.choose.left", { count: view.penaltiesLeft[game] });
  return (
    <button
      type="button"
      aria-disabled={reason !== null}
      aria-label={reason === null ? undefined : t(`king.choose.${reason}`, { game: name })}
      className={`flex min-w-0 flex-col items-start rounded-key border-[1.5px] text-start ${dense ? "px-2.5 py-1.5" : "px-3 py-2"} ${reason === null ? "border-ink/35 bg-ink/10 hover:border-gold" : "border-ink/15 bg-ink/5 opacity-40"}`}
      onClick={() => reason === null && onPick(game)}
    >
      <span className={`truncate font-extrabold ${dense ? "text-[13px]" : "text-[14px]"}`}>
        {name}
      </span>
      <span className="truncate text-[11px] text-ink/70">{t(`king.price.${game}`)}</span>
      {left === null ? null : <span className="text-[11px] font-bold text-gold">{left}</span>}
    </button>
  );
}

/** The trump game's second step: the suit, each with how many of it you hold. */
function SuitStep({
  view,
  dense,
  onBack,
}: {
  view: king.KingView;
  dense: boolean;
  onBack: () => void;
}) {
  const { t } = useT();
  return (
    <>
      <div className="grid grid-cols-4 gap-2.5">
        {SUITS.map((suit) => {
          const count = view.yourHand.filter((card) => card.suit === suit).length;
          const red = suit === "hearts" || suit === "diamonds";
          return (
            <OptionTile
              key={suit}
              glyph={<span className={red ? "text-suit-red" : "text-ink"}>{SUIT_GLYPH[suit]}</span>}
              hint={t("table.trump.inHand", { count })}
              name={`${t(`suit.${suit}`)}, ${t("table.trump.inHand", { count })}`}
              dense={dense}
              onClick={() =>
                send({ type: "move", move: { kind: "contract", game: "trump", suit } })
              }
            />
          );
        })}
      </div>
      <div className="flex justify-end">
        <Key
          label={t("king.choose.back")}
          dense
          className="flex-none rounded-pill px-6 text-[14px]"
          onClick={onBack}
        />
      </div>
    </>
  );
}

/** King: the chooser names the hand's game; a trump game asks for its suit next (review 0026 S11). */
export function KingContractSheet({
  view,
  narrow = false,
}: {
  view: king.KingView;
  narrow?: boolean;
}) {
  const { t } = useT();
  const [suitStep, setSuitStep] = useState(false);
  if (view.choices === null) return null;
  const rights = view.rights[view.yourSeat] ?? { trumps: 0, penalties: 0 };
  const pick = (game: Game) =>
    game === "trump" ? setSuitStep(true) : send({ type: "move", move: { kind: "contract", game } });
  return (
    <Sheet
      title={t(suitStep ? "king.choose.suit" : "king.choose.title")}
      aside={t("king.choose.rights", rights)}
      labelId="king-title"
      dense={narrow}
    >
      {suitStep ? (
        <SuitStep view={view} dense={narrow} onBack={() => setSuitStep(false)} />
      ) : (
        <div className={`grid gap-2 ${narrow ? "grid-cols-2" : "grid-cols-4"}`}>
          {GAMES.map((game) => (
            <GameKey key={game} view={view} game={game} dense={narrow} onPick={pick} />
          ))}
        </div>
      )}
    </Sheet>
  );
}
